import { Code, ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Checkbox, FormControlLabel, FormHelperText, TextField, Typography } from '@mui/material';
import Button from 'material/components/Button';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbarError } from 'store/actions/global';
import { useCopyToClipboard } from 'utils/hooks';
import { StyledCode } from 'globalStyles';
import WidgetDialog from 'routes/ActionCenterDetails/components/WidgetDialog';
import {
  ActionButtonsWrapper,
  InputsRow,
  InputWrapper,
  OptionsSection,
  OptionsToggle,
  PreviewWrapper,
  SignupWidgetCheckboxWrapper,
  StyledCard,
} from './styles';

/**
 * Card for generating the embeddable signup form snippet: a preview image,
 * collapsible width/height/full-height/custom-CSS options, and buttons to
 * copy the resulting `<div>` + `<script>` embed code or view it in a dialog.
 * Use on the signup details page.
 */
const SignupWidget = ({ defaultSignupId = '' }) => {
  const dispatch = useDispatch();
  const [copy] = useCopyToClipboard();
  const [showOptions, setShowOptions] = useState(false);
  const [width, setWidth] = useState('100');
  const [height, setHeight] = useState('1000');
  const [fullHeight, setFullHeight] = useState(false);
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const getWidgetCode = () => {
    const { REACT_APP_LANDING_PAGE_URL, REACT_APP_SIGNUP_WIDGET_URL } = process.env;

    return `
    <div
      class="muster-embed muster-embed-signup"
      data-sign-up-id="${defaultSignupId}"
      data-domain="${REACT_APP_LANDING_PAGE_URL}/embed-form"
      data-height="${height}"
      data-width="${width}%"
      data-height-full="${fullHeight ? 'true' : 'false'}"
      data-css="${customCssUrl}"
      id="muster-signup"
    >
      <script src="${REACT_APP_SIGNUP_WIDGET_URL}"></script>
    </div>
  `;
  };

  const validate = () => {
    if (!defaultSignupId) {
      dispatch(setSnackbarError({ message: 'Please provide a signup ID' }));

      return false;
    }

    if (customCssUrl && (!customCssUrl.startsWith('https') || !customCssUrl.endsWith('.css'))) {
      dispatch(
        setSnackbarError({ message: 'Custom CSS URL must start with https and end with .css' })
      );

      return false;
    }

    return true;
  };

  const handleCopyToClipboard = () => {
    if (!validate()) return;

    copy(getWidgetCode(), 'textarea', 'Code');
  };

  const handleViewCode = () => {
    if (!validate()) return;

    setIsDialogOpen(true);
  };

  const handleDialogCopyToClipboard = () => {
    copy(getWidgetCode(), 'textarea', 'Code');
    setIsDialogOpen(false);
  };

  return (
    <>
      <StyledCard>
        <PreviewWrapper>
          <img
            alt="Contact form widget preview"
            height="200px"
            src="/images/contact-widget-elegante.webp"
          />
        </PreviewWrapper>

        <OptionsToggle onClick={handleOptionsToggle}>
          {showOptions ? 'Hide options' : 'Options'}
          {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </OptionsToggle>

        {showOptions && (
          <OptionsSection>
            <InputsRow>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Width (%)"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-width`"
                />
              </InputWrapper>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Height (pixels)"
                  value={height}
                  onChange={e => setHeight(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-height`"
                />
              </InputWrapper>
            </InputsRow>

            <SignupWidgetCheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullHeight}
                    onChange={e => setFullHeight(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Full height</Typography>}
              />
              <FormHelperText
                sx={{
                  marginLeft: '14px',
                  marginRight: '14px',
                  marginTop: '3px',
                }}
              >
                Populates `data-height-full`
              </FormHelperText>
            </SignupWidgetCheckboxWrapper>

            <InputWrapper>
              <TextField
                fullWidth
                label="Custom CSS (Optional)"
                value={customCssUrl}
                onChange={e => setCustomCssUrl(e.target.value)}
                variant="outlined"
                size="medium"
                placeholder="https://example.com/styles.css"
                helperText="Populates `data-css`"
              />
            </InputWrapper>
          </OptionsSection>
        )}

        <ActionButtonsWrapper>
          <Button variant="text" startIcon={<ContentCopy />} onClick={handleCopyToClipboard}>
            Copy to Clipboard
          </Button>
          <Button variant="contained" startIcon={<Code />} onClick={handleViewCode}>
            View Code
          </Button>
        </ActionButtonsWrapper>
      </StyledCard>
      <WidgetDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={() => setIsDialogOpen(false)}
        title="Widget Code"
        handleCopyToClipboard={handleDialogCopyToClipboard}
      >
        <StyledCode>{getWidgetCode()}</StyledCode>
      </WidgetDialog>
    </>
  );
};

SignupWidget.propTypes = {
  /** Signup uuid baked into the snippet as `data-sign-up-id`; required to generate code. */
  defaultSignupId: PropTypes.string,
};

export default SignupWidget;
